import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  MapPin, 
  Clock, 
  Activity, 
  AlertCircle, 
  CheckCircle, 
  RefreshCw,
  Search,
  Filter
} from 'lucide-react';

interface Sentinel {
  sentinel_id: string;
  friendly_name: string;
  beacon_type: string;
  sentinel_type: string;
  time_of_assignment: string | null;
  time_of_removal: string | null;
  is_assigned: boolean;
  is_active: boolean;
  is_reporting_location: boolean;
}

interface SentinelValidation {
  sentinel_id: string;
  friendly_name: string;
  has_recent_sightings: boolean;
  sightings_count: number;
  time_of_removal: string | null;
  is_active: boolean;
  error?: string;
}

export const ListaSentinels: React.FC = () => {
  const [sentinels, setSentinels] = useState<Sentinel[]>([]);
  const [validationData, setValidationData] = useState<SentinelValidation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'reporting' | 'not-reporting'>('all');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const fetchSentinels = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Obtener sentinels disponibles
      const sentinelsResponse = await fetch('http://172.16.8.156:8000/api/tags-test', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Obtener datos de validación
      const validationResponse = await fetch('http://172.16.8.156:8000/api/sentinels-validation-debug', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (sentinelsResponse.ok && validationResponse.ok) {
        const sentinelsData = await sentinelsResponse.json();
        const validationData = await validationResponse.json();
        
        setSentinels(sentinelsData.data || []);
        setValidationData(validationData.validation_results || []);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching sentinels:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSentinels();
  }, []);

  const filteredSentinels = validationData.filter(sentinel => {
    const matchesSearch = sentinel.sentinel_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sentinel.friendly_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'reporting' && sentinel.has_recent_sightings) ||
                         (filterStatus === 'not-reporting' && !sentinel.has_recent_sightings);
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (sentinel: SentinelValidation) => {
    if (!sentinel.is_active) return 'bg-gray-500';
    if (sentinel.has_recent_sightings) return 'bg-green-500';
    return 'bg-red-500';
  };

  const getStatusText = (sentinel: SentinelValidation) => {
    if (!sentinel.is_active) return 'Inactivo';
    if (sentinel.has_recent_sightings) return 'Reportando';
    return 'Sin reportar';
  };

  const formatTimeAgo = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Hace menos de 1 hora';
    if (diffInHours < 24) return `Hace ${diffInHours} horas`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `Hace ${diffInDays} días`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Tag className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Tags</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Monitoreo y estado de todas las tags disponibles
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Última actualización: {lastUpdate.toLocaleTimeString()}
              </div>
              <button
                onClick={fetchSentinels}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Buscar por ID o nombre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                <option value="reporting">Reportando</option>
                <option value="not-reporting">Sin reportar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Tag className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Sentinels</p>
                <p className="text-2xl font-bold text-gray-900">{validationData.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Reportando</p>
                <p className="text-2xl font-bold text-gray-900">
                  {validationData.filter(s => s.has_recent_sightings).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Sin reportar</p>
                <p className="text-2xl font-bold text-gray-900">
                  {validationData.filter(s => !s.has_recent_sightings).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Activity className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Disponibles</p>
                <p className="text-2xl font-bold text-gray-900">{sentinels.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sentinels Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Lista de Sentinels ({filteredSentinels.length})
            </h3>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Cargando sentinels...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sentinel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sightings (24h)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Última Actividad
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSentinels.map((sentinel) => (
                    <tr key={sentinel.sentinel_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className={`h-3 w-3 rounded-full ${getStatusColor(sentinel)}`}></div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {sentinel.friendly_name || sentinel.sentinel_id}
                            </div>
                            <div className="text-sm text-gray-500">
                              {sentinel.sentinel_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          sentinel.has_recent_sightings 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {getStatusText(sentinel)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {/* Aquí podrías agregar el tipo de sentinel si está disponible */}
                        200 FLEX
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                          {sentinel.sightings_count}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-1" />
                          {formatTimeAgo(sentinel.time_of_removal)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredSentinels.length === 0 && (
                <div className="text-center py-12">
                  <Tag className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No hay sentinels</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No se encontraron sentinels que coincidan con los filtros aplicados.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListaSentinels;
